// Mxltiverse — Chiptune music player (Howler.js)
// Real CC0/CC tracks, crossfade between moods.

import { Howl } from 'howler';
import type { MusicalMood } from '@mxltiverse/shared';

const TRACKS: Record<MusicalMood, { url: string; name: string; source: string }> = {
  exploration: { url: '/r2/audio-preview/oga/exploration_1_adventure_begins.wav', name: 'The Adventure Begins', source: 'OGA CC0' },
  combat:      { url: '/r2/audio-preview/oga/combat_5_boss_bounce.mp3',          name: 'Boss Fight Bounce',    source: 'OGA CC0' },
  tension:     { url: '/r2/audio-preview/oga/tension_2_spooky_dungeon.ogg',      name: 'Spooky Dungeon',       source: 'OGA CC0' },
  dialogue:    { url: '/r2/audio-preview/oga/dialogue_4_chippy_melody.ogg',      name: 'Chippy Melody 1',      source: 'OGA CC0' },
  victory:     { url: '/r2/audio-preview/oga/victory_5_keep_dream.ogg',          name: 'Keep Your Dream Alive',source: 'OGA CC0' },
  defeat:      { url: '/pda/Looking%20for%20a%20new%20beginning.ogg',            name: 'Looking for a new beginning', source: 'PdA CC' },
};

const VOLUME = 0.35;
const FADE_MS = 1200;

let currentHowl: Howl | null = null;
let currentMood: MusicalMood | null = null;
let muted = false;
const cache: Partial<Record<MusicalMood, Howl>> = {};

function getHowl(mood: MusicalMood): Howl {
  if (!cache[mood]) {
    cache[mood] = new Howl({
      src: [TRACKS[mood].url],
      loop: true,
      volume: 0,
      html5: true,
    });
  }
  return cache[mood]!;
}

export const music = {
  play(mood: MusicalMood) {
    if (mood === currentMood && currentHowl?.playing()) return;

    const next = getHowl(mood);

    // Fade out current, fade in next
    if (currentHowl && currentMood) {
      const prev = currentHowl;
      prev.fade(prev.volume(), 0, FADE_MS);
      setTimeout(() => prev.pause(), FADE_MS + 50);
    }

    next.once('load', () => {
      next.volume(muted ? 0 : VOLUME);
    });

    // If already loaded, play immediately
    if (next.state() === 'loaded') {
      next.volume(muted ? 0 : VOLUME);
    } else {
      next.volume(0);
      next.once('load', () => next.fade(0, muted ? 0 : VOLUME, FADE_MS));
    }

    next.play();
    currentHowl = next;
    currentMood = mood;
  },

  stop() {
    if (currentHowl) {
      currentHowl.fade(currentHowl.volume(), 0, FADE_MS);
      setTimeout(() => currentHowl?.pause(), FADE_MS + 50);
    }
    currentMood = null;
  },

  toggleMute(): boolean {
    muted = !muted;
    if (currentHowl) {
      currentHowl.fade(currentHowl.volume(), muted ? 0 : VOLUME, 200);
    }
    return muted;
  },

  isMuted(): boolean {
    return muted;
  },
};
